import { Tent, ChevronRight, Shield, Award, Truck, Mountain, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { kategorien, zelte } from "@/data/zelte";

interface HeroProps {
  onNavigate: (path: string) => void;
}

export function Hero({ onNavigate }: HeroProps) {
  const categoryCount = kategorien.length;
  const zeltCount = zelte.length;

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 mesh-gradient" />

      {/* Floating Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large blob */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl blob" />
        <div className="absolute top-1/2 -left-32 w-80 h-80 bg-accent/20 dark:bg-accent/10 rounded-full blur-3xl float" style={{ animationDelay: '-2s' }} />
        <div className="absolute -bottom-20 right-1/4 w-72 h-72 bg-primary/8 dark:bg-primary/5 rounded-full blur-3xl float" style={{ animationDelay: '-4s' }} />

        {/* Floating icons */}
        <div className="hidden lg:block absolute top-[20%] right-[15%] float" style={{ animationDelay: '-1s' }}>
          <div className="w-14 h-14 rounded-2xl bg-card/80 backdrop-blur-sm shadow-lg flex items-center justify-center border border-border/50">
            <Mountain className="w-7 h-7 text-primary" />
          </div>
        </div>
        <div className="hidden lg:block absolute top-[60%] right-[8%] float" style={{ animationDelay: '-3s' }}>
          <div className="w-12 h-12 rounded-2xl bg-card/80 backdrop-blur-sm shadow-lg flex items-center justify-center border border-border/50">
            <Star className="w-6 h-6 text-amber-500" />
          </div>
        </div>
        <div className="hidden lg:block absolute bottom-[25%] right-[22%] float" style={{ animationDelay: '-5s' }}>
          <div className="w-10 h-10 rounded-xl bg-card/80 backdrop-blur-sm shadow-lg flex items-center justify-center border border-border/50">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
        </div>
      </div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <Container className="relative z-10">
        <div className="py-12 md:py-16 lg:py-20">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="slide-up stagger-1">
              <div className="inline-flex items-center gap-2.5 rounded-full glass px-5 py-2 text-sm font-medium text-foreground mb-8 glow-sm">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/15">
                  <Tent className="h-3.5 w-3.5 text-primary" />
                </div>
                <span>Deutschlands #1 Zelt-Vergleichsportal</span>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              </div>
            </div>

            {/* Headline */}
            <h1 className="slide-up stagger-2 text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8 leading-[1.1]">
              Finden Sie das{" "}
              <span className="relative inline-block">
                <span className="text-gradient">perfekte Zelt</span>
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-primary/30" viewBox="0 0 200 12" preserveAspectRatio="none">
                  <path d="M0,8 Q50,0 100,8 T200,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>{" "}
              <br className="hidden sm:block" />
              für Ihr Abenteuer
            </h1>

            {/* Subline */}
            <p className="slide-up stagger-3 text-xl md:text-2xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
              Vergleichen Sie{" "}
              <span className="font-semibold text-foreground">Zeltmodelle aller Kategorien</span>,
              lesen Sie unabhängige Bewertungen und finden Sie das ideale Zelt für Camping, Trekking oder Festivals.
            </p>

            {/* CTAs */}
            <div className="slide-up stagger-4 flex flex-col sm:flex-row gap-4 mb-16">
              <Button
                size="lg"
                onClick={() => onNavigate("/zelte")}
                className="btn-premium text-base h-14 px-8 rounded-xl text-primary-foreground"
              >
                <span className="relative z-10 flex items-center">
                  Alle Zelte entdecken
                  <ChevronRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => onNavigate("/finder")}
                className="text-base h-14 px-8 rounded-xl border-2 hover:bg-muted/50 transition-all duration-300"
              >
                Zelt-Finder starten
              </Button>
            </div>

            {/* Stats */}
            <div className="slide-up stagger-5 grid grid-cols-3 gap-8 max-w-lg">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-1">{categoryCount}</div>
                <div className="text-sm text-muted-foreground">Kategorien</div>
              </div>
              <div className="text-center border-x border-border/50">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-1">{zeltCount}</div>
                <div className="text-sm text-muted-foreground">Zeltmodelle</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-1">4.9</div>
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  Bewertung
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Row */}
        <div className="slide-up stagger-6 grid grid-cols-1 sm:grid-cols-3 gap-4 pb-12 max-w-3xl">
          <div className="group flex items-center gap-4 p-4 rounded-2xl glass-card card-hover cursor-default">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 dark:from-green-500/30 dark:to-emerald-500/30 flex items-center justify-center transition-transform group-hover:scale-110">
              <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="font-semibold text-sm mb-0.5">Unabhängige Tests</div>
              <div className="text-xs text-muted-foreground">Ehrliche Bewertungen</div>
            </div>
          </div>

          <div className="group flex items-center gap-4 p-4 rounded-2xl glass-card card-hover cursor-default">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 dark:from-amber-500/30 dark:to-orange-500/30 flex items-center justify-center transition-transform group-hover:scale-110">
              <Award className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <div className="font-semibold text-sm mb-0.5">Top-Marken</div>
              <div className="text-xs text-muted-foreground">Beste Qualität</div>
            </div>
          </div>

          <div className="group flex items-center gap-4 p-4 rounded-2xl glass-card card-hover cursor-default">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 dark:from-blue-500/30 dark:to-indigo-500/30 flex items-center justify-center transition-transform group-hover:scale-110">
              <Truck className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="font-semibold text-sm mb-0.5">Bester Preis</div>
              <div className="text-xs text-muted-foreground">Direkt zum Shop</div>
            </div>
          </div>
        </div>
      </Container>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
}
